import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import AssignmentStatisticsCard from './AssignmentStatisticsCard';
import EditAssignmentModal from './EditAssignmentModal';
import TeamAggregationStats from './TeamAggregationStats';
import { formatDate } from '../utils/formatters';

const AssignmentTrackingTab = ({ selectedTeam, coachId, isDesktop, isAdmin = false }) => {
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const [filters, setFilters] = useState({
    playerId: '',
    teamId: '',
    status: 'all', // all, pending, completed, overdue
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
  });

  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    overdue: 0,
  });

  useEffect(() => {
    if (isAdmin) {
      loadTeams();
      loadAssignments();
    } else if (selectedTeam) {
      loadPlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam, isAdmin]);

  useEffect(() => {
    if (!isAdmin && players.length > 0) {
      loadAssignments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, isAdmin]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, filters]);

  const loadTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name, division')
        .order('name');

      if (error) throw error;
      setTeams(data || []);
    } catch (err) {
      console.error('Error loading teams:', err);
      setError(err.message);
    }
  };

  const loadPlayers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('team_id', selectedTeam.id)
        .eq('role', 'player')
        .order('full_name');

      if (error) throw error;
      setPlayers(data || []);
    } catch (err) {
      console.error('Error loading players:', err);
      setError(err.message);
    }
  };

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError('');

      // Try with template join first, fallback without if it fails
      let data, error;

      try {
        let query = supabase.from('assigned_drills').select(
          `
            *,
            player:profiles!player_id(id, full_name, team_id),
            drill:drills!drill_id(id, name, category, difficulty_level),
            coach:profiles!assigned_by(full_name),
            template:drill_templates!template_id(id, name)
          `
        );

        // For coaches, filter by team's players
        if (!isAdmin) {
          const playerIds = players.map((p) => p.id);
          query = query.in('player_id', playerIds);
        }

        const result = await query
          .order('due_date', { ascending: true, nullsLast: true })
          .order('assigned_date', { ascending: false });

        data = result.data;
        error = result.error;
      } catch (err) {
        console.warn('Template join failed, loading without template data:', err);
        // Fallback: query without template join
        let query = supabase.from('assigned_drills').select(
          `
            *,
            player:profiles!player_id(id, full_name, team_id),
            drill:drills!drill_id(id, name, category, difficulty_level),
            coach:profiles!assigned_by(full_name)
          `
        );

        // For coaches, filter by team's players
        if (!isAdmin) {
          const playerIds = players.map((p) => p.id);
          query = query.in('player_id', playerIds);
        }

        const result = await query
          .order('due_date', { ascending: true, nullsLast: true })
          .order('assigned_date', { ascending: false });

        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      setAssignments(data || []);
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...assignments];

    // Filter by team (admin only)
    if (isAdmin && filters.teamId) {
      result = result.filter((a) => a.player?.team_id === filters.teamId);
    }

    // Filter by player
    if (filters.playerId) {
      result = result.filter((a) => a.player_id === filters.playerId);
    }

    // Filter by status
    if (filters.status !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      result = result.filter((a) => {
        if (filters.status === 'completed') return a.completed;
        if (filters.status === 'pending')
          return !a.completed && (!a.due_date || new Date(a.due_date) >= today);
        if (filters.status === 'overdue')
          return !a.completed && a.due_date && new Date(a.due_date) < today;
        return true;
      });
    }

    // Filter by search term (drill name)
    if (filters.searchTerm) {
      result = result.filter((a) =>
        a.drill.name.toLowerCase().includes(filters.searchTerm.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      result = result.filter((a) => new Date(a.assigned_date) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      result = result.filter((a) => new Date(a.assigned_date) <= new Date(filters.dateTo));
    }

    setFilteredAssignments(result);
    calculateStatistics(result);
  };

  const calculateStatistics = (data) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const stats = {
      total: data.length,
      completed: data.filter((a) => a.completed).length,
      pending: data.filter((a) => !a.completed && (!a.due_date || new Date(a.due_date) >= today))
        .length,
      overdue: data.filter((a) => !a.completed && a.due_date && new Date(a.due_date) < today)
        .length,
    };

    setStatistics(stats);
  };

  const handleEdit = (assignment) => {
    setSelectedAssignment(assignment);
    setShowEditModal(true);
  };

  const handleDelete = async (assignment) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this assignment?\n\nPlayer: ${assignment.player.full_name}\nDrill: ${assignment.drill.name}`
      )
    ) {
      return;
    }

    try {
      const { error } = await supabase.from('assigned_drills').delete().eq('id', assignment.id);

      if (error) throw error;

      // Reload assignments
      await loadAssignments();
    } catch (err) {
      console.error('Error deleting assignment:', err);
      setError(err.message);
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedAssignment(null);
    loadAssignments();
  };

  const handleClearFilters = () => {
    setFilters({
      playerId: '',
      teamId: '',
      status: 'all',
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  const getStatusBadge = (assignment) => {
    if (assignment.completed) {
      return <span style={styles.badge.completed}>Completed</span>;
    }

    if (assignment.due_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(assignment.due_date);

      if (dueDate < today) {
        return <span style={styles.badge.overdue}>Overdue</span>;
      }
    }

    return <span style={styles.badge.pending}>Pending</span>;
  };

  const styles = {
    container: {
      flex: 1,
    },
    filtersCard: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '24px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
    },
    filtersTitle: {
      fontSize: isDesktop ? '18px' : '16px',
      fontWeight: 'bold',
      marginBottom: '16px',
      color: '#333',
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : '1fr',
      gap: isDesktop ? '16px' : '12px',
      marginBottom: '16px',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#666',
    },
    input: {
      padding: '10px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      minHeight: '44px',
    },
    select: {
      padding: '10px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: '#fff',
      minHeight: '44px',
    },
    dateRangeGrid: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr',
      gap: isDesktop ? '16px' : '12px',
    },
    clearButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: '#f5f5f5',
      color: '#333',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
    },
    assignmentsCard: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '24px' : '16px',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    cardTitle: {
      fontSize: isDesktop ? '20px' : '18px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    count: {
      fontSize: isDesktop ? '16px' : '14px',
      color: '#666',
    },
    tableContainer: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '800px',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '2px solid #e0e0e0',
      fontWeight: '600',
      color: '#666',
      fontSize: '14px',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '14px',
    },
    badge: {
      pending: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        textTransform: 'uppercase',
        backgroundColor: '#fff3e0',
        color: '#f57c00',
      },
      completed: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        textTransform: 'uppercase',
        backgroundColor: '#e8f5e9',
        color: '#388e3c',
      },
      overdue: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        textTransform: 'uppercase',
        backgroundColor: '#ffebee',
        color: '#d32f2f',
      },
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    button: {
      padding: isDesktop ? '6px 12px' : '10px 16px',
      fontSize: isDesktop ? '12px' : '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
    },
    editButton: {
      backgroundColor: '#007bff',
      color: '#fff',
    },
    deleteButton: {
      backgroundColor: '#dc3545',
      color: '#fff',
    },
    card: {
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
    },
    cardRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    cardLabel: {
      fontSize: '12px',
      color: '#666',
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    cardValue: {
      fontSize: '14px',
      color: '#333',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#999',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      marginBottom: '16px',
      border: '1px solid #fcc',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.emptyState, padding: '60px 20px' }}>Loading assignments...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {isAdmin && <TeamAggregationStats assignments={assignments} teams={teams} />}

      <AssignmentStatisticsCard statistics={statistics} />

      <div style={styles.filtersCard}>
        <h3 style={styles.filtersTitle}>Filters</h3>

        <div style={styles.filtersGrid}>
          {isAdmin && (
            <div style={styles.filterGroup}>
              <label style={styles.label}>Team</label>
              <select
                style={styles.select}
                value={filters.teamId}
                onChange={(e) => setFilters({ ...filters, teamId: e.target.value })}
              >
                <option value="">All Teams</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.filterGroup}>
            <label style={styles.label}>Player</label>
            <select
              style={styles.select}
              value={filters.playerId}
              onChange={(e) => setFilters({ ...filters, playerId: e.target.value })}
            >
              <option value="">All Players</option>
              {players.map((player) => (
                <option key={player.id} value={player.id}>
                  {player.full_name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>Status</label>
            <select
              style={styles.select}
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>Search Drill</label>
            <input
              type="text"
              style={styles.input}
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              placeholder="Search by drill name..."
            />
          </div>
        </div>

        <div style={styles.dateRangeGrid}>
          <div style={styles.filterGroup}>
            <label style={styles.label}>Assigned From</label>
            <input
              type="date"
              style={styles.input}
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.label}>Assigned To</label>
            <input
              type="date"
              style={styles.input}
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            />
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <button style={styles.clearButton} onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.assignmentsCard}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>Assignments</h3>
          <span style={styles.count}>({filteredAssignments.length})</span>
        </div>

        {filteredAssignments.length === 0 ? (
          <div style={styles.emptyState}>
            {filters.playerId || filters.status !== 'all' || filters.searchTerm
              ? 'No assignments match your filters.'
              : 'No assignments found for this team.'}
          </div>
        ) : isDesktop ? (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {isAdmin && <th style={styles.th}>Team</th>}
                  <th style={styles.th}>Player</th>
                  <th style={styles.th}>Drill</th>
                  <th style={styles.th}>Routine</th>
                  <th style={styles.th}>Assigned</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Sets/Reps</th>
                  <th style={styles.th}>Difficulty</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.map((assignment) => {
                  // Find team name for this assignment (admin only)
                  const teamName = isAdmin
                    ? teams.find((t) => t.id === assignment.player?.team_id)?.name || 'Unknown'
                    : null;

                  return (
                    <tr key={assignment.id}>
                      {isAdmin && <td style={styles.td}>{teamName}</td>}
                      <td style={styles.td}>{assignment.player.full_name}</td>
                      <td style={styles.td}>{assignment.drill.name}</td>
                      <td style={styles.td}>
                        {assignment.template ? (
                          assignment.template.name
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>{formatDate(assignment.assigned_date)}</td>
                      <td style={styles.td}>
                        {assignment.due_date ? formatDate(assignment.due_date) : 'No due date'}
                      </td>
                      <td style={styles.td}>
                        {assignment.sets || 3} x {assignment.reps || 10}
                      </td>
                      <td style={styles.td}>{assignment.difficulty_level || 'intermediate'}</td>
                      <td style={styles.td}>{getStatusBadge(assignment)}</td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button
                            style={{ ...styles.button, ...styles.editButton }}
                            onClick={() => handleEdit(assignment)}
                          >
                            Edit
                          </button>
                          <button
                            style={{ ...styles.button, ...styles.deleteButton }}
                            onClick={() => handleDelete(assignment)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            {filteredAssignments.map((assignment) => {
              // Find team name for this assignment (admin only)
              const teamName = isAdmin
                ? teams.find((t) => t.id === assignment.player?.team_id)?.name || 'Unknown'
                : null;

              return (
                <div key={assignment.id} style={styles.card}>
                  <div style={styles.cardRow}>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {assignment.player.full_name}
                    </span>
                    {getStatusBadge(assignment)}
                  </div>

                  {isAdmin && (
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Team</span>
                      <span style={styles.cardValue}>{teamName}</span>
                    </div>
                  )}

                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>Drill</span>
                    <span style={styles.cardValue}>{assignment.drill.name}</span>
                  </div>

                  {assignment.template && (
                    <div style={styles.cardRow}>
                      <span style={styles.cardLabel}>Routine</span>
                      <span style={styles.cardValue}>{assignment.template.name}</span>
                    </div>
                  )}

                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>Assigned</span>
                    <span style={styles.cardValue}>{formatDate(assignment.assigned_date)}</span>
                  </div>

                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>Due Date</span>
                    <span style={styles.cardValue}>
                      {assignment.due_date ? formatDate(assignment.due_date) : 'No due date'}
                    </span>
                  </div>

                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>Sets/Reps</span>
                    <span style={styles.cardValue}>
                      {assignment.sets || 3} x {assignment.reps || 10}
                    </span>
                  </div>

                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>Difficulty</span>
                    <span style={styles.cardValue}>
                      {assignment.difficulty_level || 'intermediate'}
                    </span>
                  </div>

                  <div style={{ ...styles.actionButtons, marginTop: '12px' }}>
                    <button
                      style={{ ...styles.button, ...styles.editButton, flex: 1 }}
                      onClick={() => handleEdit(assignment)}
                    >
                      Edit
                    </button>
                    <button
                      style={{ ...styles.button, ...styles.deleteButton, flex: 1 }}
                      onClick={() => handleDelete(assignment)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showEditModal && selectedAssignment && (
        <EditAssignmentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          assignment={selectedAssignment}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default AssignmentTrackingTab;
